import { EmptyState, formatNumber } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import type { ReactNode } from 'react';
import { healthOfLevel, sumAvailable, type Location, type StockLevel } from './inventory.ts';
import styles from './StockMatrix.module.css';

export type StockRow = {
  readonly variantId: string;
  readonly label: string;
  readonly detail?: string;
  readonly price?: string;
};

const NO_THRESHOLD = '—';

const thresholdOf = (levels: readonly StockLevel[]): number | null => {
  const thresholds = levels.flatMap((level) =>
    level.lowStockThreshold === null ? [] : [level.lowStockThreshold],
  );
  return thresholds.length === 0 ? null : Math.min(...thresholds);
};

const thresholdOrDash = (threshold: number | null, count: (value: number) => string): string =>
  threshold === null ? NO_THRESHOLD : count(threshold);

const availableIn = (levels: readonly StockLevel[], variantId: string, locationId: string) =>
  levels.find((level) => level.variantId === variantId && level.locationId === locationId);

export type StockMatrixProps<Row extends StockRow> = {
  readonly labelledBy?: string | undefined;
  readonly rows: readonly Row[];
  readonly locations: readonly Location[];
  readonly levels: readonly StockLevel[];
  readonly showThreshold?: boolean;
  readonly actionsOf?: ((row: Row) => ReactNode) | undefined;
  readonly totalLabel?: string | undefined;
  readonly showLegend?: boolean;
};

export const StockMatrix = <Row extends StockRow>({
  labelledBy,
  rows,
  locations,
  levels,
  showThreshold = false,
  actionsOf,
  totalLabel: footLabel,
  showLegend = true,
}: StockMatrixProps<Row>) => {
  const { t, i18n } = useTranslation();
  const count = (value: number) => formatNumber(value, i18n.language);
  const totalLabel = t('stockMatrix.total');

  if (locations.length === 0)
    return <EmptyState title={t('inventory.noDepots')} description={t('inventory.noDepotsHelp')} />;

  const rowLevels = (variantId: string) => levels.filter((level) => level.variantId === variantId);
  const columnLevels = (locationId: string) => levels.filter((level) => level.locationId === locationId);

  return (
    <div className={styles['frame']}>
      <table className={styles['matrix']} aria-labelledby={labelledBy}>
        {labelledBy === undefined ? (
          <caption className="visually-hidden">{t('stockMatrix.caption')}</caption>
        ) : null}
        <thead>
          <tr>
            <th scope="col">{t('inventory.variant')}</th>
            {locations.map((location) => (
              <th key={location.id} scope="col" className={styles['number']}>
                {location.name}
              </th>
            ))}
            <th scope="col" className={styles['number']}>
              {totalLabel}
            </th>
            {showThreshold ? (
              <th scope="col" className={styles['number']}>
                {t('stockMatrix.threshold')}
              </th>
            ) : null}
            {actionsOf === undefined ? null : (
              <th scope="col" className={styles['number']}>
                {t('catalog.actions')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.variantId}>
              <th scope="row">
                <span className={styles['variant']}>
                  <span>{row.label}</span>
                  {row.detail === undefined ? null : <span className={styles['detail']}>{row.detail}</span>}
                </span>
              </th>
              {locations.map((location) => {
                const level = availableIn(levels, row.variantId, location.id);
                const health = healthOfLevel(level);
                return (
                  <td key={location.id} data-label={location.name} className={styles['number']}>
                    <span
                      className={[styles['level'], styles[`level_${health}`]].join(' ')}
                      title={health === 'ok' ? undefined : t(`stockMatrix.${health}`)}
                    >
                      <span className={[styles['dot'], styles[health]].join(' ')} aria-hidden="true" />
                      {count(level?.available ?? 0)}
                      {health === 'ok' ? null : (
                        <span className="visually-hidden">{t(`stockMatrix.${health}`)}</span>
                      )}
                    </span>
                  </td>
                );
              })}
              <td data-label={totalLabel} className={[styles['number'], styles['strong']].join(' ')}>
                {count(sumAvailable(rowLevels(row.variantId)))}
              </td>
              {showThreshold ? (
                <td
                  data-label={t('stockMatrix.threshold')}
                  className={[styles['number'], styles['muted']].join(' ')}
                >
                  {thresholdOrDash(thresholdOf(rowLevels(row.variantId)), count)}
                </td>
              ) : null}
              {actionsOf === undefined ? null : <td className={styles['actions']}>{actionsOf(row)}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">{footLabel ?? totalLabel}</th>
            {locations.map((location) => (
              <td key={location.id} data-label={location.name} className={styles['number']}>
                {count(sumAvailable(columnLevels(location.id)))}
              </td>
            ))}
            <td data-label={totalLabel} className={[styles['number'], styles['strong']].join(' ')}>
              {count(sumAvailable(levels))}
            </td>
            {showThreshold ? <td aria-hidden="true" /> : null}
            {actionsOf === undefined ? null : <td aria-hidden="true" />}
          </tr>
        </tfoot>
      </table>
      {showLegend ? <StockLegend /> : null}
    </div>
  );
};

export const StockLegend = ({ children }: { children?: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <p className={styles['legend']}>
      {(['ok', 'low', 'out'] as const).map((health) => (
        <span key={health}>
          <span className={[styles['dot'], styles[health]].join(' ')} aria-hidden="true" />
          {t(`stockMatrix.legend_${health}`)}
        </span>
      ))}
      {children}
    </p>
  );
};
